import { HighlightOff, PlaylistAddCheck } from "@mui/icons-material";
import { Badge, Menu, MenuItem } from "@mui/material";
import { FC, MouseEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  FloatingMenuButton,
  FloatingMenuTooltipWrapper,
  FramerTooltip,
  MuiButton,
} from "@ryde/controls";
import {
  useBookingsBulkActions,
  useDynamicFormsFetch,
  useModalConfirm,
  usePageContext,
  useSelectedIds,
} from "@ryde/redux";
import {
  bulkOperationsDrawerAF,
  ConfirmResult,
  DirectionEnum,
  DrawerType,
  getDirectionAndBookingIds,
  isUtcDateBeforeToday,
  Log,
  selectedBookingCalendarDatesSelector,
  selectedCellsBookingCalendarAtom,
  selectedCellsBookingsSelector,
  useDrawers,
  useHasPermissions,
  useIsMobile,
  useTimezone,
} from "@ryde/shared";
import { greyMui } from "@ryde/theme";

interface IBookingsFloatingMenuCalendarInnerProps {
  onRender: () => void;
}

export const BookingsFloatingMenuCalendarInner: FC<
  IBookingsFloatingMenuCalendarInnerProps
> = ({ onRender }) => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const { timezone } = useTimezone();
  const { entity } = usePageContext();
  const { clearSelectedIds } = useSelectedIds(entity);
  const { confirmDialogResult } = useModalConfirm();
  const { bulkArchive, bulkCancel, cleanupPreviousProductionData } =
    useBookingsBulkActions();
  const { openDrawer, openBookingProduction } = useDrawers();

  const bookingDtos = useRecoilValue(selectedCellsBookingsSelector);
  const { fetchTableRowsServerFiltered } = useDynamicFormsFetch(
    "booking",
    "bookings",
  );
  const [selectedBookingCellIds, setSelectedBookingCellIds] = useRecoilState(
    selectedCellsBookingCalendarAtom,
  );
  const selectedBookingCalendarDates = useRecoilValue(
    selectedBookingCalendarDatesSelector,
  );

  const selectedBookingIds = useMemo(() => {
    return bookingDtos.map(bookingDto => bookingDto.id);
  }, [bookingDtos]);

  const setBulkOperationsDrawerState = useSetRecoilState(
    bulkOperationsDrawerAF(entity),
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  /*  for code commented below:  const [csvAnchorEl, setCsvAnchorEl] = useState<null | HTMLElement>(null); */
  const open = Boolean(anchorEl);
  /* for code commented below:  const csvMenuOpen = Boolean(csvAnchorEl); */
  const { hasPermissions: hasPermissionCreateInThePast } = useHasPermissions({
    entity: "booking",
    action: "createInPast",
  });
  const { hasPermissions: canProduce } = useHasPermissions({
    entity: "booking",
    action: "produce",
  });

  const isSelectedDateInThePast = useMemo(() => {
    for (const date of selectedBookingCalendarDates) {
      if (isUtcDateBeforeToday(date, timezone)) {
        return true;
      }
    }

    return false;
  }, [selectedBookingCalendarDates, timezone]);

  const isCreateBookingDisabled =
    !hasPermissionCreateInThePast && isSelectedDateInThePast;

  const onClearSelectionHandler = useEvent((): void => {
    setSelectedBookingCellIds(new Set());
  });

  /* PLEASE DO NOT DELETE - WE MAY NEED TO RETURN THIS METHODS
    const handleCsvClick = (event: MouseEvent<HTMLElement>): void => {
      setCsvAnchorEl(event.currentTarget);
    };

    const handleCsvMenuClose = async (): Promise<void> => {
      setCsvAnchorEl(null);
    };
 */

  const handleArchiveClick = (event: MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleArchiveClose = async (
    e: MouseEvent<HTMLLIElement>,
  ): Promise<void> => {
    const { directionValue } = e.currentTarget.dataset;
    setAnchorEl(null);

    if (!directionValue) {
      return;
    }

    const selectedBookingIdsToArchive =
      directionValue === DirectionEnum.Both
        ? bookingDtos
            ?.filter(
              booking => booking.direction === "A" || booking.direction === "B",
            )
            .map(filteredBooking => filteredBooking.id)
        : bookingDtos
            ?.filter(booking => booking.direction === directionValue)
            .map(filteredBooking => filteredBooking.id);
    const bookingsCount = selectedBookingIdsToArchive?.length;

    if (!bookingsCount) {
      Log.infoAndToast(t("booking.bulk.noBookingsSelected"));
    } else {
      const confirmationResult = await confirmDialogResult({
        title: t("general.CONFIRM"),
        message: t("booking.bulk.bulkBookingArchiveConfirmationMessage", {
          bookingsCount,
          directionValue,
        }),
      });

      if (confirmationResult === ConfirmResult.OK) {
        Log.debug(`Bulk archive action for ids`, selectedBookingIdsToArchive);
        const bulkArchiveResult = await bulkArchive(
          selectedBookingIdsToArchive,
        );

        if (bulkArchiveResult.success) {
          fetchTableRowsServerFiltered();
        }
      }
    }
  };

  const handleProduceClick = async (
    event: MouseEvent<HTMLElement>,
  ): Promise<void> => {
    const [direction, bookingsIds] = getDirectionAndBookingIds(bookingDtos);
    const handleProduceResult = await openBookingProduction({
      bookingsIds,
      direction,
    });

    if (handleProduceResult.success) {
      fetchTableRowsServerFiltered();
      clearSelectedIds();
    }
  };

  const handleOpenBulkOperationsDrawerClick = (): void => {
    cleanupPreviousProductionData();
    setBulkOperationsDrawerState(true);
  };

  const handleCancelClick = useEvent(async () => {
    if (!selectedBookingIds.length) {
      return;
    }

    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.CANCEL_BOOKINGS_MESSAGE`),
    });

    if (confirmationResult === ConfirmResult.OK) {
      const bulkCancelResult = await bulkCancel(selectedBookingIds);

      if (bulkCancelResult.success) {
        onClearSelectionHandler();
        fetchTableRowsServerFiltered();
      }
    }
  });

  useEffect(() => {
    onRender();
  }, [onRender]);

  return (
    !!selectedBookingCellIds.size && (
      <>
        <Badge color="primary" badgeContent={selectedBookingCellIds.size}>
          <PlaylistAddCheck style={{ color: greyMui._700 }} />
        </Badge>

        <FloatingMenuTooltipWrapper tooltip={t("booking.bulk.cellClear")}>
          <FloatingMenuButton onClick={onClearSelectionHandler}>
            <HighlightOff sx={{ color: greyMui._700 }} />
          </FloatingMenuButton>
        </FloatingMenuTooltipWrapper>
        {!isMobile && (
          <>
            {canProduce && (
              <FloatingMenuButton onClick={handleProduceClick}>
                <SVG_MUI.ProductionSVG color={greyMui._700} />
                <span>{t("booking.bulk.produce")}</span>
              </FloatingMenuButton>
            )}

            {/* PLEASE DO NOT DELETE - WE MAY NEED TO RETURN THIS METHODS */}
            {/* <FloatingMenuButton
              aria-controls={
                csvMenuOpen ? "upload-download-csv-menu" : undefined
              }
              aria-haspopup="true"
              aria-expanded={csvMenuOpen ? "true" : undefined}
              onClick={handleCsvClick}
            >
              <SVG_MUI.UploadSVG />
              <span>{`${t("booking.bulk.downloadCSV")}`}</span>
            </FloatingMenuButton>

            <Menu
              id="upload-download-csv-menu"
              anchorEl={csvAnchorEl}
              open={csvMenuOpen}
              onClose={handleCsvMenuClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <MenuItem onClick={handleCsvMenuClose}>
                <CSVLink
                  data={bookingDtos}
                  headers={bulkBookingsCSVHeadersTranslated}
                  filename={`${t(`booking.${PLURAL_KEY}`)}.csv`}
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <MuiButton muiVariant="text" onClick={() => null}>
                    {t(`general.EXPORT_CSV`)}
                  </MuiButton>
                </CSVLink>
              </MenuItem>
            </Menu> */}

            <MuiButton
              onClick={handleArchiveClick}
              startIcon={<SVG_MUI.ArchiveSVG />}
              muiVariant="outlined"
              muiStyle="error"
            >
              <span>{t(`general.ARCHIVE`)}</span>
            </MuiButton>

            <Menu
              id="bulk-archive-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleArchiveClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
            >
              <MenuItem
                data-direction-value={DirectionEnum.Both}
                onClick={handleArchiveClose}
              >
                {t("booking.bulk.archiveAll")}
              </MenuItem>
              <MenuItem
                data-direction-value={DirectionEnum.PickUp}
                onClick={handleArchiveClose}
              >
                {t("booking.bulk.archiveDirection", {
                  directionValue: "A",
                })}
              </MenuItem>
              <MenuItem
                data-direction-value={DirectionEnum.DropOff}
                onClick={handleArchiveClose}
              >
                {t("booking.bulk.archiveDirection", {
                  directionValue: "B",
                })}
              </MenuItem>
            </Menu>
          </>
        )}

        <FloatingMenuButton onClick={handleCancelClick}>
          {t(`general.BULK_CANCEL`)}
        </FloatingMenuButton>

        <FramerTooltip
          tooltipDisabled={!isCreateBookingDisabled}
          content={t("booking.THERE_ARE_SOME_DATES_IN_THE_PAST_SELECTED")}
        >
          <MuiButton
            muiStyle="success"
            className="openDrawer"
            muiSize="small"
            disabled={isCreateBookingDisabled}
            onClick={() =>
              openDrawer({
                drawerProps: {
                  type: DrawerType.BulkCreateBookings,
                },
              })
            }
          >
            {t("booking.bulk.create")}
          </MuiButton>
        </FramerTooltip>

        <MuiButton
          muiStyle="secondary"
          className="openDrawer"
          muiSize="small"
          onClick={handleOpenBulkOperationsDrawerClick}
        >
          <SVG_MUI.EllipsisVertical3SVG color="white" />
        </MuiButton>
      </>
    )
  );
};
